import React from 'react';
import { StyleSheet, View, Text, ActivityIndicator, ScrollView, Platform } from 'react-native';
import { useTheme } from '../../../../theme/useTheme';
import { WARNING_RED } from '../../constants/colors';
import HeatTableCard, { HeatTableRow } from './components/HeatTableCard';
import KCPHPerformanceCard, { KCPHGroupedRow } from './components/KCPHPerformanceCard';
import MetricCard from './components/MetricCard';
import SchedulingTrendCard, { SchedulingTrendData } from './components/SchedulingTrendCard';
import ToolAdoptionBulletCard, { BulletChartRow } from './components/ToolAdoptionBulletCard';
import type { ChartDrillLevel, ChartTimeframeGrain } from './crewingAnalyticsStaticData';
import type { CrewingKpiMetric, ScheduleHealthRow, SchedulingTrendResponse, KcphPerformanceResponse, ToolAdoptionRow } from './crewingAnalyticsApi';
import type { AsyncState } from './useCrewingAnalytics';
import { LOCATION_HIERARCHY_MAP, parseTimeframeLabel } from './crewingAnalyticsStaticData';

type AsyncStateOf<T> = AsyncState<T>;

// Human-readable grouping label per drill level, used to keep chart titles/row headers in sync
// with the actual Region/Market/Location grouping selected in Chart Configuration.
const DRILL_LEVEL_LABEL: Record<ChartDrillLevel, string> = {
  region: 'Region / Unit',
  market: 'Market',
  location: 'Location',
};

const TIMEFRAME_LABEL: Record<ChartTimeframeGrain, string> = {
  year: 'Year',
  period: 'Period',
  week: 'Week',
  day: 'Day',
};

// Info-tooltip copy for the 7 Executive Overview KPI tiles — verbatim from the Crewing Analytics mockup.
const KPI_TIPS: Record<string, string> = {
  'Crewing Accuracy': 'Definition: how closely scheduled hours matched worked hours. Calculation: Scheduled Hours / Actual (Kronos) Hours. Target: 95% or higher. Note: capped at 100; overage mirrors back (105% reads as 95%).',
  'Crewing Efficiency': 'Definition: how closely the labor estimate matched worked hours. Calculation: Estimated Hours / Actual (Kronos) Hours. Target: 95% or higher. Note: capped at 100; overage mirrors back.',
  'Crewing Compliance': 'Definition: how closely the schedule matched the forecast. Calculation: Forecasted Hours / Scheduled Hours. Target: 95% or higher. Note: capped at 100; overage mirrors back.',
  'Estimated KCPH': 'Definition: the crewing model standard rate for the scope. Calculation: Estimated Cases / Scheduled Hours. Target: aggregated Site Standard KCPH for the selected scope. Note: engineered reference standard.',
  'Scheduled KCPH': 'Definition: the productivity the schedule was built to. Calculation: Scheduled Cases / Scheduled Hours. Target: aggregated Site Standard KCPH for the selected scope.',
  'Actual KCPH': 'Definition: actual cases moved per actual labor hour worked. Calculation: Actual Cases / Actual (Kronos) Hours. Target: aggregated Site Standard KCPH for the selected scope. Note: blue at or above target, red below.',
  'Crewing Tool Adoption %': 'Definition: share of in-scope locations actively using the scheduling tool. Calculation: active unique location IDs in the timeframe / total unique location IDs. Target: 80% or higher.',
};

interface ExecutiveOverviewProps {
  drillLevel?: ChartDrillLevel;
  timeframe?: ChartTimeframeGrain;
  selectedRegions?: string[];
  selectedMarkets?: string[];
  selectedLocations?: string[];
  // Deprecated: props kept for backwards compatibility, but not used when drillLevel is set
  scheduleHealthRows?: HeatTableRow[];
  kcphPerformanceRows?: KCPHGroupedRow[];
  toolAdoptionRows?: BulletChartRow[];
  // API data as AsyncState (loading/error/data)
  apiKpiMetrics?: AsyncStateOf<CrewingKpiMetric[]>;
  // Real prior-period KPI values used to compute the tiles' "vs prior" delta ourselves — the
  // backend's own kpiOverview.delta/trendDirection turned out to be (value - target), not a real
  // prior comparison, so those fields are ignored in favor of this.
  apiPriorKpiMetrics?: AsyncStateOf<CrewingKpiMetric[]>;
  apiScheduleHealthRows?: AsyncStateOf<ScheduleHealthRow[]>;
  apiSchedulingTrend?: AsyncStateOf<SchedulingTrendResponse>;
  apiKcphPerformanceRows?: AsyncStateOf<KcphPerformanceResponse>;
  apiToolAdoptionRows?: AsyncStateOf<ToolAdoptionRow[]>;
  // "vs prior" comparison data (prior period, or prior year when the chart timeframe grain is 'year')
  apiPriorScheduleHealthRows?: AsyncStateOf<ScheduleHealthRow[]>;
  apiPriorKcphPerformanceRows?: AsyncStateOf<KcphPerformanceResponse>;
  apiPriorToolAdoptionRows?: AsyncStateOf<ToolAdoptionRow[]>;
}

const ExecutiveOverview: React.FC<ExecutiveOverviewProps> = ({
  drillLevel = 'region',
  timeframe = 'period',
  selectedRegions = [],
  selectedMarkets = [],
  selectedLocations = [],
  scheduleHealthRows: _scheduleHealthRows,
  kcphPerformanceRows: _kcphPerformanceRows,
  toolAdoptionRows: _toolAdoptionRows,
  apiKpiMetrics,
  apiPriorKpiMetrics,
  apiScheduleHealthRows,
  apiSchedulingTrend,
  apiKcphPerformanceRows,
  apiToolAdoptionRows,
  apiPriorScheduleHealthRows,
  apiPriorKcphPerformanceRows,
  apiPriorToolAdoptionRows,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const normalize = (value: string) => value.trim().toLowerCase();

  // The 3 KCPH KPI tiles (Estimated/Scheduled/Actual) must show the same aggregated scope target
  // used by the Efficiency Breakdown table and the KCPH Performance chart below, not the backend's
  // fixed 90 fallback on the kpi-overview endpoint.
  const aggregatedKcphTarget = apiKcphPerformanceRows?.data?.target;
  const KCPH_TILE_NAMES = ['Estimated KCPH', 'Scheduled KCPH', 'Actual KCPH'];

  // Helper to render loading state
  const renderLoading = () => (
    <View style={[styles.loadingContainer, isDark && { backgroundColor: '#202020' }]}>
      <ActivityIndicator size="large" color="#0B5FC6" />
      <Text style={[styles.loadingText, isDark && { color: '#B0B0B0' }]}>Loading data...</Text>
    </View>
  );

  // Helper to render error state
  const renderError = (title: string, error: string) => (
    <View style={[styles.errorContainer, isDark && { backgroundColor: '#3A1414', borderLeftColor: '#C0392B' }]}>
      <Text style={[styles.errorTitle, isDark && { color: '#F28B82' }]}>{title}</Text>
      <Text style={[styles.errorText, isDark && { color: '#B0B0B0' }]}>{error || 'Unable to load data'}</Text>
    </View>
  );

  // Helper to render empty state
  const renderEmpty = (title: string) => (
    <View style={[styles.emptyContainer, isDark && { backgroundColor: '#202020' }]}>
      <Text style={[styles.emptyTitle, isDark && { color: '#B0B0B0' }]}>{title}</Text>
      <Text style={[styles.emptyText, isDark && { color: '#6E6E6E' }]}>No data available</Text>
    </View>
  );

  // Render KPI Metrics section
  const renderKpiSection = () => {
    if (apiKpiMetrics?.loading) return renderLoading();
    // On API failure keep the normal layout: same 7 tiles with placeholder values.
    if (apiKpiMetrics?.error) {
      return (
        <View style={styles.kpiGrid}>
          {Object.keys(KPI_TIPS).map((name) => (
            <View key={name} style={styles.kpiCell}>
              <MetricCard title={name} value="--" infoTooltip={KPI_TIPS[name]} />
            </View>
          ))}
        </View>
      );
    }
    if (!apiKpiMetrics?.data || apiKpiMetrics.data.length === 0) return renderEmpty('KPI Metrics');

    const formatApiValue = (m: CrewingKpiMetric) =>
      m.format === 'percent' ? `${m.value.toFixed(1)}%` : m.value.toFixed(1);
    // KCPH tiles use the aggregated scope target (falls back to the metric's own target until
    // the kcph-performance fetch resolves); every other tile keeps its own backend target as-is.
    const effectiveTarget = (m: CrewingKpiMetric) =>
      KCPH_TILE_NAMES.includes(m.name) && aggregatedKcphTarget != null ? aggregatedKcphTarget : m.target;
    const apiValueColor = (m: CrewingKpiMetric) => m.value >= effectiveTarget(m) ? '#0B5FC6' : WARNING_RED;

    // Real "vs prior" delta, computed client-side from a separate prior-timeframe KPI fetch —
    // NOT the backend's own delta/trendDirection (which is actually value-minus-target). Only
    // present when the prior fetch actually ran (single year/period/week selected); otherwise
    // there's nothing to compare against and the tile shows no trend line at all.
    const priorByName = new Map((apiPriorKpiMetrics?.data ?? []).map((m) => [m.name, m]));

    const liveMetrics = apiKpiMetrics.data.map((m) => {
      const target = effectiveTarget(m);
      const prior = priorByName.get(m.name);
      const priorDelta = prior ? m.value - prior.value : null;
      const priorTrendDirection: 'up' | 'down' | undefined =
        priorDelta == null ? undefined : priorDelta > 0 ? 'up' : priorDelta < 0 ? 'down' : undefined;
      return {
        title: m.name,
        value: formatApiValue(m),
        description: `Target: ${m.format === 'percent' ? `${target.toFixed(1)}%` : target.toFixed(1)}`,
        valueColor: apiValueColor(m),
        trendText: priorDelta == null
          ? undefined
          : `${m.format === 'percent' ? `${Math.abs(priorDelta).toFixed(1)}%` : Math.abs(priorDelta).toFixed(1)} vs prior`,
        trendDirection: priorTrendDirection,
        trendImpact: (m.value >= target ? 'positive' : 'negative') as 'positive' | 'negative',
        infoTooltip: KPI_TIPS[m.name],
      };
    });

    return (
      <View style={styles.kpiGrid}>
        {liveMetrics.map((metric) => (
          <View key={metric.title} style={styles.kpiCell}>
            <MetricCard
              title={metric.title}
              value={metric.value}
              description={metric.description}
              valueColor={metric.valueColor}
              trendText={metric.trendText}
              trendDirection={metric.trendDirection}
              trendImpact={metric.trendImpact}
              infoTooltip={metric.infoTooltip}
            />
          </View>
        ))}
      </View>
    );
  };

  // Render Schedule Health & Scheduling Trend
  const renderScheduleSection = () => {
    const sheetLoading = apiScheduleHealthRows?.loading;
    const sheetError = apiScheduleHealthRows?.error;
    const sheetData = apiScheduleHealthRows?.data;

    const trendLoading = apiSchedulingTrend?.loading;
    const trendError = apiSchedulingTrend?.error;
    const trendData = apiSchedulingTrend?.data;

    if (sheetLoading || trendLoading) return renderLoading();
    // On API failure keep the normal layout: the cards render with empty data
    // (their own empty states) instead of being replaced by an error banner.
    if (!sheetError && (!sheetData || sheetData.length === 0)) return renderEmpty('Schedule Health');
    if (!trendError && !trendData) return renderEmpty('Scheduling Trend');

    const safeSheetData: HeatTableRow[] = (!sheetError && Array.isArray(sheetData) ? sheetData : []).map((row) => ({
      label: String(row?.label ?? '--'),
      accuracy: Number(row?.accuracy ?? 0),
      efficiency: Number(row?.efficiency ?? 0),
      compliance: Number(row?.compliance ?? 0),
      kcphIndex: Number(row?.kcphIndex ?? 0),
    }));

    const groupLabel = DRILL_LEVEL_LABEL[drillLevel];

    // Prior-period comparison is best-effort: if it's still loading or failed, the charts simply
    // render without delta annotations rather than blocking on it.
    const priorSheetData = Array.isArray(apiPriorScheduleHealthRows?.data) ? apiPriorScheduleHealthRows!.data! : [];
    const safePriorSheetData: HeatTableRow[] = priorSheetData.map((row) => ({
      label: String(row?.label ?? '--'),
      accuracy: Number(row?.accuracy ?? 0),
      efficiency: Number(row?.efficiency ?? 0),
      compliance: Number(row?.compliance ?? 0),
      kcphIndex: Number(row?.kcphIndex ?? 0),
    }));

    const trend = trendError ? undefined : trendData;
    const safeTrendData: SchedulingTrendData = {
      periods: Array.isArray(trend?.periods) ? trend!.periods.map((p) => parseTimeframeLabel(String(p ?? '--'), timeframe).main) : [],
      periodSubLabels: Array.isArray(trend?.periods) ? trend!.periods.map((p) => parseTimeframeLabel(String(p ?? '--'), timeframe).sub) : [],
      accuracy: Array.isArray(trend?.accuracy) ? trend!.accuracy.map((v) => Number(v ?? 0)) : [],
      efficiency: Array.isArray(trend?.efficiency) ? trend!.efficiency.map((v) => Number(v ?? 0)) : [],
      compliance: Array.isArray(trend?.compliance) ? trend!.compliance.map((v) => Number(v ?? 0)) : [],
      kcphIndex: Array.isArray(trend?.kcphIndex) ? trend!.kcphIndex.map((v) => Number(v ?? 0)) : [],
    };

    return (
      <View style={[styles.section2Col, Platform.OS === 'web' ? null : styles.stackMobile]}>
        <View style={styles.chartCell}>
          <HeatTableCard
            title="Schedule Health"
            subtitle={`by ${groupLabel}`}
            description="Scheduling Target: 95% / KCPH Index Target: 100%"
            rows={safeSheetData}
            priorRows={safePriorSheetData}
            rowLabelHeader={groupLabel}
          />
        </View>
        <View style={styles.chartCell}>
          <SchedulingTrendCard
            title="Scheduling Trend"
            subtitle={`by ${TIMEFRAME_LABEL[timeframe]}`}
            description="%"
            data={safeTrendData}
          />
        </View>
      </View>
    );
  };

  // Render KCPH Performance & Tool Adoption
  const renderKcphSection = () => {
    const kcphLoading = apiKcphPerformanceRows?.loading;
    const kcphError = apiKcphPerformanceRows?.error;
    const kcphData = apiKcphPerformanceRows?.data?.rows;

    const toolLoading = apiToolAdoptionRows?.loading;
    const toolError = apiToolAdoptionRows?.error;
    const toolData = apiToolAdoptionRows?.data;

    if (kcphLoading || toolLoading) return renderLoading();
    // On API failure keep the normal layout: the cards render with empty data.
    if (!kcphError && (!kcphData || kcphData.length === 0)) return renderEmpty('KCPH Performance');
    if (!toolError && (!toolData || toolData.length === 0)) return renderEmpty('Tool Adoption');

    const safeKcphData: KCPHGroupedRow[] = (!kcphError && Array.isArray(kcphData) ? kcphData : []).map((row) => ({
      label: String(row?.label ?? '--'),
      estimated: Number(row?.estimated ?? 0),
      scheduled: Number(row?.scheduled ?? 0),
      actual: Number(row?.actual ?? 0),
    }));

    const safeToolData: BulletChartRow[] = (!toolError && Array.isArray(toolData) ? toolData : []).map((row) => ({
      label: String(row?.label ?? '--'),
      value: Number(row?.value ?? 0),
    }));

    const groupLabel = DRILL_LEVEL_LABEL[drillLevel];

    // Prefer the kcph-performance endpoint's own scope target; fall back to the Actual KCPH KPI
    // tile's target until the backend starts sending one directly on this endpoint.
    const kcphTarget = aggregatedKcphTarget
      ?? apiKpiMetrics?.data?.find((m) => m.name === 'Actual KCPH')?.target
      ?? 90;

    const priorKcphData = Array.isArray(apiPriorKcphPerformanceRows?.data?.rows) ? apiPriorKcphPerformanceRows!.data!.rows : [];
    const safePriorKcphData: KCPHGroupedRow[] = priorKcphData.map((row) => ({
      label: String(row?.label ?? '--'),
      estimated: Number(row?.estimated ?? 0),
      scheduled: Number(row?.scheduled ?? 0),
      actual: Number(row?.actual ?? 0),
    }));

    const priorToolData = Array.isArray(apiPriorToolAdoptionRows?.data) ? apiPriorToolAdoptionRows!.data! : [];
    const safePriorToolData: BulletChartRow[] = priorToolData.map((row) => ({
      label: String(row?.label ?? '--'),
      value: Number(row?.value ?? 0),
    }));

    return (
      <View style={[styles.section2Col, Platform.OS === 'web' ? null : styles.stackMobile]}>
        <View style={styles.chartCell}>
          <KCPHPerformanceCard
            title="KCPH Performance"
            subtitle={`by ${groupLabel}`}
            description="Cases Per Hour"
            rows={safeKcphData}
            priorRows={safePriorKcphData}
            target={kcphTarget}
          />
        </View>
        <View style={styles.chartCell}>
          <ToolAdoptionBulletCard
            title="Crewing Tool Adoption"
            subtitle={`by ${groupLabel}`}
            description="% of Active Locations"
            rows={safeToolData}
            priorRows={safePriorToolData}
            target={80}
          />
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {/* 7 KPI Cards */}
      {renderKpiSection()}

      {/* Schedule Health & Scheduling Trend */}
      {renderScheduleSection()}

      {/* KCPH Performance & Tool Adoption */}
      {renderKcphSection()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  loadingContainer: {
    padding: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    minHeight: 200,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
  },
  emptyContainer: {
    padding: 30,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fafafa',
    borderRadius: 8,
    minHeight: 150,
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#999',
  },
  emptyText: {
    fontSize: 12,
    color: '#bbb',
    marginTop: 4,
  },
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: 10,
    width: '100%',
  },
  kpiCell: {
    flex: 1,
    minWidth: 0,
    minHeight: 124,
  },
  section2Col: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  stackMobile: {
    flexDirection: 'column',
  },
  chartCell: {
    flex: 1,
    minHeight: 360,
    overflow: 'hidden',
    maxWidth: '100%',
    maxHeight: 420,
  },

});

export default ExecutiveOverview;
